// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useState, useEffect, Fragment, useRef } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { withStyles } from '@material-ui/styles';
import { capitalize, get } from 'lodash';
import { push } from 'connected-react-router';
import { arrayOf, bool, func, number, shape, string } from 'prop-types';

import {
  AlertDialog, Footer, RadioGroupSection,
  StatusIndicator, TabPanel, TabsContainer,
} from '../../../components';
import { getTabIndex, hasCRUDPermissions, hasError } from '../../../utils';
import { getI18n, getI18nFormattedMessage } from '../../../utils/intl';
import {
  PROCEED_TEXT,
  ALERT_SUCCESS,
  SUCCESS,
  PERMISSION_PRODUCT,
  PERMISSION_ORGANIZATIONS_PRODUCT,
  PERMISSION_APIS_PRODUCT,
  PERMISSION_TIERS_PRODUCT,
  PERMISSION_APPLICATIONS_PRODUCT,
  PRODUCT_ACCESS_TYPE_PRIVATE,
  PRODUCT_ACCESS_TYPE_DIALOG_LIST,
  ASSIGNED,
} from '../../../constants';
import {
  fetchProduct,
  deleteProduct,
  fetchApplications,
} from '../../../actions/products';
import {
  getIsError,
  getErrors,
  getIsLoading,
  getproductDetails,
  getIsProductSaveSuccess,
  getDeleteProductStatus,
  getAppsListTotalElements,
} from '../../../reducers/products';
import {
  getConfig,
  getUserDetails,
} from '../../../reducers/portalConfig';
import { DetailsHeader } from '../../../components';
import DetailsContainer from '../../details';
import Overview from './Overview';
import Apis from './Apis';
import Organizations from './Organizations';
import styles from './styles';
import RateQuota from './RateQuota';
import Applications from './Applications';
import Spec from './Spec';
import Documentation from '../../../pageComponents/Documentations';
import { fetchApiEulas } from '../../../actions/api';

const getTabItems = (productUuid,
  hasVisibleRLQ, hasVisibleApi, hasVisibleOrgs, hasVisibleApps) => [
  {
    id: 'overview',
    tabId: 'products-details-overview-tab',
    label: getI18nFormattedMessage('label.overview'),
    visible: true,
  },
  {
    id: 'rate-quota',
    tabId: 'products-details-rate-quota-tab',
    label: getI18nFormattedMessage('label.rate.limit.quotas'),
    visible: !!productUuid && hasVisibleRLQ,
  },
  {
    id: 'apis',
    tabId: 'products-details-apis-tab',
    label: getI18nFormattedMessage('label.apis'),
    visible: !!productUuid && hasVisibleApi,
  },
  {
    id: 'organizations',
    tabId: 'products-details-organizations-tab',
    label: getI18nFormattedMessage('label.organizations'),
    visible: !!productUuid && hasVisibleOrgs,
  },
  {
    id: 'applications',
    tabId: 'products-details-applications-tab',
    label: getI18nFormattedMessage('label.applications'),
    visible: !!productUuid && hasVisibleApps,
  },
  {
    id: 'spec',
    tabId: 'products-details-spec-tab',
    label: getI18nFormattedMessage('label.spec'),
    visible: !!productUuid && hasVisibleApi,
  },
  {
    id: 'documentation',
    tabId: 'products-details-documentation-tab',
    label: getI18nFormattedMessage('label.documentation'),
    visible: !!productUuid,
  },
];

const getTabs = (tabItems) => tabItems.filter(({ visible }) => visible);

export const ProductsDetails = (props) => {
  const {
    isLoading,
    userContext,
    pageErrors,
    isError,
    productDetails,
    classes,
    history,
    isProductSaveSuccess,
    productDeleteSuccess,
    fetchApplications,
    appsListTotalElements,
  } = props;

  const productUuid = get(props, 'match.params.productUuid');
  const pageHeaderRef = useRef(null);
  const intl = getI18n(useIntl());
  const [productName, setProductName] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isFieldEdit, setIsFieldEdit] = useState(!productUuid);
  const [showDelete, setShowDelete] = useState(!productUuid);
  const [isFieldEditIcon, setIsFieldEditIcon] = useState(!productUuid);
  const [isFieldEditName, setIsFieldEditName] = useState(false);
  const [isProductNameError, setIsProductNameError] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const [errors, setErrors] = useState([]);

  const hasDelete = hasCRUDPermissions(userContext, ['DELETE'], PERMISSION_PRODUCT);
  const hasUpdate = hasCRUDPermissions(userContext, ['UPDATE'], PERMISSION_PRODUCT);
  const hasVisibleRLQ = hasCRUDPermissions(userContext, ['READ'], PERMISSION_TIERS_PRODUCT);
  const hasVisibleApi = hasCRUDPermissions(userContext, ['READ'], PERMISSION_APIS_PRODUCT);
  const hasVisibleOrgs = hasCRUDPermissions(userContext, ['READ'], PERMISSION_ORGANIZATIONS_PRODUCT);
  const hasVisibleApps = hasCRUDPermissions(userContext, ['READ'], PERMISSION_APPLICATIONS_PRODUCT);
  const hasVisibleDocs = hasCRUDPermissions(userContext, ['READ'], PERMISSION_PRODUCT);

  const tabItems = getTabItems(productUuid, hasVisibleRLQ, hasVisibleApi,
    hasVisibleOrgs, hasVisibleApps);
  const tabs = getTabs(tabItems);
  const defaultIndex = getTabIndex(tabs, props.match);
  const [redirectTabValue, setRedirectTabValue] = useState(tabs[defaultIndex].tabId);
  const [tabIndex, setTabIndex] = useState(defaultIndex);
  const [currentTab, setCurrentTab] = useState(tabs[defaultIndex].tabId);
  const [accessStatus, setAccessStatus] = useState(PRODUCT_ACCESS_TYPE_PRIVATE);
  const [accessStatusChangeValue, setAccessStatusChangeValue] =
    useState(PRODUCT_ACCESS_TYPE_PRIVATE);
  const [showChangeAccessDialog, setShowChangeAccessDialog] = useState(false);

  useEffect(() => {
    window.scrollTo(0, pageHeaderRef.offsetTop);
    localStorage.setItem('isUnSavedChanges-products', false);
    if (!hasCRUDPermissions(userContext, ['READ'], PERMISSION_PRODUCT)) {
      props.push('/404');
    }
  }, [userContext]);

  useEffect(() => {
    if (productDeleteSuccess === SUCCESS) {
        notifyMessages(ALERT_SUCCESS, intl.getI18nMessage('label.product.delete.success'));
        window.location.href = '/admin/products';
    }
  }, [productDeleteSuccess]);

  useEffect(() => {
    if (isProductSaveSuccess || productUuid) {
      setIsFieldEdit(false);
      setIsFieldEditName(false);
      setShowDelete(true);
      if(productUuid) {
        props.fetchProduct(productUuid);
      }
      if(isProductSaveSuccess) {
        setTimeout(() => { notifyMessages('', '')}, 2500);
      }
    }
  }, [isProductSaveSuccess, productUuid]);

  useEffect(() => {
    if (currentTab === 'products-details-overview-tab') {
      if(productUuid){
        props.fetchProduct(productUuid);
        fetchApplications({ uuid: productUuid, assign: ASSIGNED, page: 0, size: 24 })
      }
      props.fetchApiEulas();
    }
  }, [currentTab]);

  useEffect(() => {
    if(productDetails && productDetails.name) {
      setProductName(productDetails.name);
    }
    if(productDetails && productDetails.accessStatus) {
      setAccessStatus(capitalize(productDetails.accessStatus));
      setAccessStatusChangeValue(capitalize(productDetails.accessStatus));
    }
  }, [productDetails]);

  useEffect(() => {
     const defaultIndex = getTabIndex(tabs, props.match);
      setTabIndex(defaultIndex);
      setCurrentTab(tabs[defaultIndex].tabId);
      if(tabs[defaultIndex].tabId === 'products-details-overview-tab') {
        setIsFieldEditIcon(true);
      } else {
        setIsFieldEditIcon(false);
      }
  }, [props.match]);

  const notifyMessages = (status, message) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
  };

  useEffect(() => {
    setIsFieldEditName(false);
    setErrors(isError ? pageErrors : []);
  }, [isError, pageErrors]);

  const handleChange = (value) => {
    localStorage.setItem('isUnSavedChanges-products', true);
    const nameUiError = hasError(value, true);
    setIsFieldEditName(true);
    setProductName(value);
    setIsProductNameError(nameUiError);
  }

  const onClickEditIcon = () => setIsFieldEdit(true);

  const deleteFn = () => {
    props.deleteProduct(productUuid);
  };

  const onHideEditTitle = () => {
    setIsFieldEditName(false);
    setProductName(get(productDetails, 'name'));
    setIsFieldEdit(false);
  }

  const resetAccessStatus = () => {
    setAccessStatus(capitalize(get(productDetails, 'accessStatus')));
    setAccessStatusChangeValue(capitalize(get(productDetails, 'accessStatus')));
  }

  const handleTabChange = (e, newValue) => {
    const hasUnsavedChanges = localStorage.getItem('isUnSavedChanges-products');
    if (hasUnsavedChanges === 'true') {
      setIsDialogOpen(true);
      setRedirectTabValue(newValue);
    } else {
      if(tabs[newValue].tabId !== 'products-details-overview-tab') {
        setIsFieldEditIcon(false);
        onHideEditTitle();
        resetAccessStatus();
      }
      setTabIndex(newValue);
      setCurrentTab(tabs[newValue].tabId);
      history.push(`/admin/products/details/${productUuid}/${tabs[newValue].id}`)
    }
  };

  const onDialogClose = () => {
    setIsDialogOpen(false);
  };

  const onDialogSubmit = () => {
    onHideEditTitle();
    resetAccessStatus();
    localStorage.setItem('isUnSavedChanges-products', false);
    setIsDialogOpen(false);
    setTabIndex(redirectTabValue);
    setCurrentTab(tabs[redirectTabValue].tabId);
    history.push(`/admin/products/details/${productUuid}/${tabs[redirectTabValue].id}`)
  };

  const onChangeAccessButtonClick = () => {
    setShowChangeAccessDialog(true);
  }

  const Status = () =>
    <StatusIndicator
      access={accessStatus}
      changeAccess={currentTab === 'products-details-overview-tab'}
      changeButtonLabel={intl.getI18nMessage('label.change')}
      onChangeAccessClick={onChangeAccessButtonClick}
      isChangeAccessButtonDisable={!hasUpdate}
    />;

  const AccessChangeDialog = () => {
    return (
      <RadioGroupSection
        selected={accessStatusChangeValue}
        onChange={(e) => setAccessStatusChangeValue(e.target.value)}
        data={PRODUCT_ACCESS_TYPE_DIALOG_LIST}
        id='product-details-change-access-dialog-radio-selection'
        defaultSelect={accessStatusChangeValue}
      />
    )
  }

  const onSubmitChangeAccessDialog = () => {
    if(accessStatus !== accessStatusChangeValue) {
      localStorage.setItem('isUnSavedChanges-products', true)
      setAccessStatus(accessStatusChangeValue);
    }
    setShowChangeAccessDialog(false)
  }

  const onCancelChangeAccessDialog = () => {
    localStorage.setItem('isUnSavedChanges-products', false)
    resetAccessStatus();
    setShowChangeAccessDialog(false)
  }

  const getErrors = () => {
    // Excluding error which is handled by APIs tab.
    const ERR_API_IN_USE = "error.product.api.in.use.by.application"
    return errors.filter(err => err.key !== ERR_API_IN_USE);
  }

  return (
    <>
      <AlertDialog
        isOpen={showChangeAccessDialog}
        title={intl.getI18nMessage('label.product.details.change.access.dialog.title')}
        description={''}
        onClose={onCancelChangeAccessDialog}
        onSubmit={onSubmitChangeAccessDialog}
        onCancel={onCancelChangeAccessDialog}
        dialogId={'product-details-change-access-status-dialog'}
        submitText={intl.getI18nMessage('label.apply.button')}
        cancelText={intl.getI18nMessage('label.cancel.button')}
        component={<AccessChangeDialog />}
      />
      <DetailsContainer
        detailsPageId="products-details-page"
        isLoading={isLoading}
        dialogId="products-unsaved-dialog"
        isDialogOpen={isDialogOpen}
        onDialogClose={onDialogClose}
        dialogSubmitText={PROCEED_TEXT}
        onDialogSubmit={onDialogSubmit}
        notificationId="products-details-notifications"
        notificationStatus={notificationStatus}
        setNotificationStatus={setNotificationStatus}
        notificationMessage={notificationMessage}
        setNotificationMessage={setNotificationMessage}
        notificationContainerClass={classes.notificationContainer}
        hasNotificationIcon={true}
        isActionsEnabled={false}
        errors={getErrors()}
        pageHeaderRef={pageHeaderRef}
        header={
          <DetailsHeader
            headerId="products-details-header-container"
            title={intl.getI18nMessage('label.product')}
            type="textField"
            handleChange={handleChange}
            name={productName}
            helperText={intl.getI18nMessage('label.product.name.help')}
            isNameError={isProductNameError}
            isFieldEdit={isFieldEdit}
            isFieldEditIcon={isFieldEditIcon && hasUpdate}
            onClickEditIcon={onClickEditIcon}
            deleteFn={deleteFn}
            showDelete={productUuid && showDelete && hasDelete}
            statusComponent={<Status/>}
            hideTextLabel={true}
          />
        }
        tabs={
          <TabsContainer
            id={'products-details-tab-container'}
            tabValue={tabIndex}
            orientation="horizontal"
            tabItems={tabs}
            handleTabChange={handleTabChange}
          />
        }
        page={
          <Fragment>
            <TabPanel
              visible={currentTab === 'products-details-overview-tab'}
              id="products-overview-tab-panel"
            >
              <Overview
                {...props}
                isCurrentTab={currentTab === 'products-details-overview-tab'}
                productUuid={productUuid}
                notifyMessages={notifyMessages}
                productName={productName}
                accessStatus={accessStatus}
                resetAccessStatus={resetAccessStatus}
                productDetails={productDetails}
                isFieldEditName={isFieldEditName}
                onHideEditTitle={onHideEditTitle}
                hasUpdate={hasUpdate}
                isProductAssignedToAnyApp={appsListTotalElements > 0}
              />
            </TabPanel>
            <TabPanel
              visible={currentTab === 'products-details-rate-quota-tab' && hasVisibleRLQ}
              id="products-details-rate-quota-tab-panel"
            >
              <RateQuota
                {...props}
                productUuid={productUuid}
                isCurrentTab={currentTab === 'products-details-rate-quota-tab'}
                notifyMessages={notifyMessages}
              />
            </TabPanel>
            <TabPanel
              visible={currentTab === 'products-details-apis-tab' && hasVisibleApi}
              id="products-details-apis-tab-panel"
            >
              <Apis
                {...props}
                isCurrentTab={currentTab === 'products-details-apis-tab'}
                productUuid={productUuid}
                tabValue={tabIndex}
                notifyMessages={notifyMessages}
              />
            </TabPanel>
            <TabPanel
              visible={currentTab === 'products-details-organizations-tab' && hasVisibleOrgs}
              id="products-details-orgs-tab-panel"
            >
              <Organizations
                {...props}
                isCurrentTab={currentTab === 'products-details-organizations-tab'}
                productUuid={productUuid}
                notifyMessages={notifyMessages}
              />
            </TabPanel>
            <TabPanel
              visible={currentTab === 'products-details-applications-tab' && hasVisibleApps}
              id="products-details-apps-tab-panel"
            >
              <Applications
                {...props}
                isCurrentTab={currentTab === 'products-details-applications-tab'}
                productUuid={productUuid}
                notifyMessages={notifyMessages}
                tabValue={currentTab}
              />
            </TabPanel>
            <TabPanel
                visible={currentTab === 'products-details-spec-tab'}
                id="products-details-spec-tab-panel"
              >
                <Spec
                  {...props}
                  apiUuid={productUuid}
                  productUuid={productUuid}
                  isCurrentTab={currentTab === 'products-details-spec-tab'}
                  tabValue={currentTab}
                />
              </TabPanel>
            <TabPanel
              visible={currentTab === 'products-details-documentation-tab' && hasVisibleDocs}
              id="products-details-documentation-tab-panel"
            >
              <Documentation
                {...props}
                entityUuid={productUuid}
                entityType="product"
                idPrefix="products-details"
                tabValue={currentTab}
                userCanEdit={hasUpdate}
                userCanDelete={hasDelete}
                secondaryEntityType={'product-api'}
              />
            </TabPanel>
          </Fragment>
        }
        footer={
          <Footer />
        }
      />
    </>
  );
};

const mapStateToProps = state => ({
  config: getConfig(state),
  isLoading: getIsLoading(state),
  isError: getIsError(state),
  pageErrors: getErrors(state),
  userContext: getUserDetails(state),
  productDetails: getproductDetails(state),
  isProductSaveSuccess: getIsProductSaveSuccess(state),
  productDeleteSuccess: getDeleteProductStatus(state),
  appsListTotalElements: getAppsListTotalElements(state),
});

const mapDispatchToProps = {
  fetchProduct,
  push,
  deleteProduct,
  fetchApiEulas,
  fetchApplications,
};

ProductsDetails.propTypes = {
  config: shape({}),
  userContext: shape({}),
  history: shape({}),
  isLoading: bool,
  isError: bool,
  pageErrors: arrayOf(shape({})),
  match: shape({}),
  push: func,
  productDetails: shape({}),
  isProductSaveSuccess: bool,
  classes: shape({}),
  fetchProduct: func,
  deleteProduct: func,
  productDeleteSuccess: string,
  fetchApiEulas: func,
  fetchApplications : func,
  appsListTotalElements : number,
};

ProductsDetails.displayName = 'ProductsDetails';

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(ProductsDetails);
